import React, { useState } from "react";
import {
  Box,
  Typography,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Z } from "../../../theme/zIndex";
import AppDialog from "../../shared/components/AppDialog";

// Componentes modulares
import ReservationFilters, { DateRange } from "./ReservationFilters";
import ReservationTable from "./ReservationTable";
import ReservationCards from "./ReservationCards";

// Hooks y tipos
import { Reservation } from "../types/Reservation";
import {
  UserPermissions,
  EditRestrictionHook,
  RestrictionModalProps,
} from "../types/interfaces";

// Servicios
import { logger } from "../../../utils/logger";

export interface ReservationQuickFilters {
  origen: string;
  saldo: string;
  departamento: string;
  movimiento: string;
  orden: string;
}

export interface ReservationSummary {
  con_saldo: number;
  saldadas: number;
  a_favor: number;
  entradas_hoy: number;
  salidas_hoy: number;
  pendiente_total: number;
}

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface ReservationsListProps {
  reservations?: Reservation[];
  onEdit: (reservation: Reservation) => void;
  onDelete: (reservation: Reservation) => void;
  onVoucher: (reservation: Reservation) => void;
  onReservationUpdate: (reservation: Reservation) => void;
  permissions: UserPermissions;
  pagination?: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearch?: (search: string) => void;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  onClearFilters?: () => void;
  quickFilters?: ReservationQuickFilters;
  onQuickFiltersChange?: (filters: ReservationQuickFilters) => void;
  summary?: ReservationSummary;
  // 🔥 NUEVO: dateRange como prop para evitar estado duplicado
  dateRange: DateRange;
  // Props para dependencias externas
  editRestrictionHook?: EditRestrictionHook;
  RestrictionModalComponent?: React.ComponentType<RestrictionModalProps>;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const ReservationsList: React.FC<ReservationsListProps> = ({
  reservations: propReservations,
  onEdit,
  onDelete,
  onVoucher,
  permissions,
  pagination,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onDateRangeChange,
  onClearFilters,
  quickFilters,
  onQuickFiltersChange,
  summary,
  dateRange, // 🔥 NUEVO: dateRange como prop
  editRestrictionHook,
  RestrictionModalComponent,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ============================================================================
  // ESTADOS LOCALES
  // ============================================================================

  const [searchTerm, setSearchTerm] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reservationToDelete, setReservationToDelete] =
    useState<Reservation | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [_deleteError, setDeleteError] = useState<string | null>(null);

  // 🔥 ELIMINADO: Estado duplicado del dateRange - se maneja en el componente padre

  // ============================================================================
  // HOOKS EXTERNOS
  // ============================================================================

  // Usar las reservas pasadas como props (el componente padre maneja la carga)
  const reservations = propReservations || [];
  const loading = false; // El loading se maneja en el componente padre
  const error = null; // El error se maneja en el componente padre

  // Debug: verificar qué reservas estamos recibiendo (temporal)
  // logger.debug("🔍 ReservationsListNew Debug:", {
  //   propReservationsLength: propReservations?.length || 0,
  //   reservationsLength: reservations.length,
  //   hasPropReservations: !!propReservations,
  // });

  // ============================================================================
  // MANEJADORES DE EVENTOS
  // ============================================================================

  // Función para manejar el clic en editar con restricciones
  const handleEditClick = (reservation: Reservation) => {
    if (editRestrictionHook) {
      const canEdit = editRestrictionHook.handleEditAttempt();
      if (canEdit) {
        onEdit(reservation);
      }
      // Si no puede editar, el modal se abrirá automáticamente
    } else {
      onEdit(reservation);
    }
  };

  const handleDeleteClick = (reservation: Reservation) => {
    setReservationToDelete(reservation);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (reservationToDelete) {
      setDeleteLoading(true);
      setDeleteError(null);
      try {
        await onDelete(reservationToDelete);
        setConfirmDelete(false);
        setReservationToDelete(null);
      } catch (_error) {
        setDeleteError("Error al eliminar la reserva");
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchTerm);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    // Llamar a la función del componente padre
    if (onClearFilters) {
      onClearFilters();
    }
  };

  // ============================================================================
  // FILTRADO Y PROCESAMIENTO DE DATOS
  // ============================================================================

  // El filtro de búsqueda se maneja en el servidor, no necesitamos filtrar localmente
  const filteredReservations = reservations;

  // ============================================================================
  // RENDERIZADO DE ESTADOS DE CARGA Y ERROR
  // ============================================================================

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100dvh",
        }}
      >
        <Typography>Cargando reservas...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100dvh",
        }}
      >
        <Typography color="error">Error al cargar las reservas</Typography>
      </Box>
    );
  }

  // ============================================================================
  // RENDERIZADO PRINCIPAL
  // ============================================================================

  return (
    <>
      {/* Filtros */}
      <ReservationFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={handleSearch}
        dateRange={dateRange}
        onDateRangeChange={(newDateRange) => {
          logger.debug("🔍 ReservationsListNew - Actualizando dateRange:", {
            newDateRange,
            currentDateRange: dateRange,
          });
          // 🔥 SIMPLIFICADO: Solo llamar a la función del componente padre
          if (onDateRangeChange) {
            onDateRangeChange(newDateRange.startDate, newDateRange.endDate);
          }
        }}
        onClearFilters={handleClearFilters}
        quickFilters={quickFilters}
        onQuickFiltersChange={onQuickFiltersChange}
        summary={summary}
        showDateFilter={true} // Habilitar el filtro de fechas
      />

      {/* Contenido principal */}
      {isMobile ? (
        <ReservationCards
          reservations={filteredReservations}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onVoucher={onVoucher}
          permissions={permissions}
          searchTerm={searchTerm}
        />
      ) : (
        <ReservationTable
          reservations={filteredReservations}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onVoucher={onVoucher}
          permissions={permissions}
          pagination={pagination}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          searchTerm={searchTerm}
        />
      )}

      {/* Dialog de confirmación de eliminación */}
      <AppDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{ zIndex: Z.modal }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ flexShrink: 0 }}>Confirmar eliminación</DialogTitle>
        <DialogContent sx={{ minHeight: 0, overflowY: "auto", flex: 1 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción no se puede deshacer
          </Alert>
          ¿Estás seguro de que deseas eliminar la reserva de{" "}
          <strong>
            {reservationToDelete?.nombre} {reservationToDelete?.apellido}
          </strong>
          ?
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, borderTop: 1, borderColor: "divider", bgcolor: "background.paper" }}>
          <Button onClick={() => setConfirmDelete(false)} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            autoFocus
            disabled={deleteLoading}
          >
            {deleteLoading ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </AppDialog>

      {/* Modal de restricción de edición */}
      {RestrictionModalComponent && editRestrictionHook && (
        <RestrictionModalComponent
          open={editRestrictionHook.isRestrictionModalOpen}
          onClose={editRestrictionHook.closeRestrictionModal}
          message={editRestrictionHook.restrictionMessage}
          contactPerson="Mariela Mendes Nunes"
        />
      )}

    </>
  );
};

export default ReservationsList;
